import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import { Container } from '@mui/material';

import OfferBreadcrumbs from '~/components/Common/Elements/OfferBreadcrumbs';
import PageHeader from '~/components/Common/Elements/PageHeader';

import ReservationService from '~/services/ReservationService';
import VendorsService from '~/services/VendorsService';

import ImagesForm from './ImagesForm';
import cleanProperty from './helpers/cleanProperty';

export default function ImagesEditPage() {
  const { id_property: propertyId, id_vendor: vendorId } = useParams<{ id_property: string; id_vendor: string }>();

  const [property, setProperty] = useState(null);
  const [vendorName, setVendorName] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([ReservationService.getProperty(propertyId), VendorsService.getVendorById(vendorId)])
      .then(([propFetch, vendorFetch]) => {
        setProperty(cleanProperty(propFetch.result));
        setVendorName(vendorFetch.result?.vendor_name || vendorFetch.result?.name || '');
        setLoading(false);
      })
      .catch((err) => {
        enqueueSnackbar(err.message, { variant: 'error' });
      });
  }, [enqueueSnackbar, propertyId, vendorId]);

  const handleImagesChange = useCallback(
    (images) => {
      setProperty({
        ...property,
        images,
      });
    },
    [property],
  );

  const createImage = useCallback(
    async (image) => {
      return ReservationService.createPropertyImages(image, property.id);
    },
    [property],
  );

  const updateImages = useCallback(
    async (images) => {
      const imagesWithPropertyId = images.map((img) => ({ ...img, property_id: property.id }));
      return ReservationService.updatePropertyImages(imagesWithPropertyId, property.id);
    },
    [property],
  );

  const deleteImage = useCallback(
    async (propertyImageId) => {
      return ReservationService.deletePropertyImage(property.id, propertyImageId);
    },
    [property],
  );

  return (
    <Container maxWidth="xl">
      <OfferBreadcrumbs
        vendor={{ label: vendorName || 'Vendor', href: `/vendors/${vendorId}` }}
        property={{
          label: property?.name || 'Property',
          href: `/vendors/${vendorId}/properties/${propertyId}`,
        }}
        currentPage="Images"
      />
      <PageHeader title="Edit property images" />
      {loading && <div>Loading...</div>}
      {!loading && (
        <ImagesForm
          images={property.images}
          onImagesChange={handleImagesChange}
          createImage={createImage}
          updateImages={updateImages}
          deleteImage={deleteImage}
          propertyId={propertyId}
        />
      )}
    </Container>
  );
}
